<script setup lang="ts">
import Logo from '~/components/icon/Logo.vue';
import { useLocalStorage } from '@vueuse/core';

const props = defineProps<{ overlayOnly?: boolean }>();

const { $viewport } = useNuxtApp();
const viewportReady = ref(false);
const desktop = computed(() => !props.overlayOnly && viewportReady.value && $viewport.isGreaterOrEquals('lg'));
onMounted(() => {
  viewportReady.value = true;
});

const sideNavOpenPermanent = useLocalStorage('sideNavOpenPermanent', true);
const sideNavHover = ref(false);
const mobileNavOpen = useState('mobileNavOpen', () => false);
const appVersion = useRuntimeConfig().public.appVersion;
const { data: session, signOut } = useAuth();
const { unsubscribeFromPushBackendOnly } = usePushNotifications();
const isNative = useIsNativePlatform();

const handleLogout = async () => {
  await unsubscribeFromPushBackendOnly();
  useLocationStore().$reset();
  useCustomerStore().$reset();
  linkClicked();
  if (isNative) {
    await signOut({ redirect: false });
    await navigateTo('/login', { external: true });
  } else {
    signOut({ callbackUrl: '/login' });
  }
};

const sideNavOpen = computed(() => {
  if (!viewportReady.value) return false;
  return (sideNavOpenPermanent.value && desktop.value) || sideNavHover.value || mobileNavOpen.value;
});

const sideNavHalfOpen = computed(() => {
  if (!viewportReady.value) return false;
  return desktop.value && !sideNavHover.value && !sideNavOpenPermanent.value;
});

const linkClicked = () => {
  if (!desktop.value) {
    sideNavHover.value = false;
    mobileNavOpen.value = false;
  }
};
</script>

<template>
  <Transition
    enter-from-class="translate-x-[-100%] w-0"
    enter-to-class="translate-x-0 w-48"
    enter-active-class="transition-all duration-500"
    leave-from-class="translate-x-0 w-48"
    leave-to-class="translate-x-[-100%] w-0"
    leave-active-class="transition-all duration-500"
    @mouseenter="sideNavHover = true"
    @mouseleave="sideNavHover = false"
  >
    <div
      v-if="session && (sideNavOpen || sideNavHalfOpen)"
      id="side-nav"
      class="flex flex-col gap-4 bg-gray-100 dark:bg-gray-800 top-0 overflow-hidden overflow-y-auto"
      :class="{
        'shrink-0 sticky transition-[width] duration-800': desktop,
        'fixed z-20': !desktop,
        'w-14': sideNavHalfOpen,
        'w-58': sideNavOpen,
      }"
      style="height: 100dvh"
    >
      <div v-if="desktop || sideNavOpen">
        <!-- Mobile header -->
        <div v-if="!desktop" class="mt-4 mb-4 w-0 min-w-full overflow-hidden text-nowrap text-ellipsis flex flex-col items-center">
          <Logo class="w-24" />
          <div class="truncate overflow-hidden text-sm text-neutral mb-0">{{ session.email }}</div>
          <TextBrand
            class="text-2xl truncate px-4 w-full overflow-hidden text-center"
            :title="$t('header.externalId', { externalId: session.organization.customerNumber })"
            :text="session.organization.name"
          /><br />
        </div>

        <!-- Desktop header -->
        <div v-else class="text-xs text-neutral align-top pt-5 mb-12 flex flex-col items-center">
          <Logo :class="{ 'w-20': sideNavHalfOpen, 'w-24': sideNavOpen }" />
          <span>{{ sideNavOpen ? session.email : '&nbsp;' }}</span>
        </div>

        <TreeNavigation :side-nav-half-open="sideNavHalfOpen" :side-nav-open="sideNavOpen" class="mx-2 mb-0" @link-clicked="linkClicked" />
      </div>
      <div v-show="sideNavOpen || sideNavHalfOpen" class="flex-grow"></div>
      <div v-if="sideNavOpen || sideNavHalfOpen" class="flex flex-col pb-5 mx-2 space-y-2" :class="{ 'items-center': !sideNavOpen }">
        <UColorModeSwitch
          class="text-primary whitespace-nowrap"
          :label="sideNavOpen ? $t('nav.main.util.darkMode') : ''"
          :class="{ 'mx-2.5': sideNavOpen }"
        >
        </UColorModeSwitch>
        <UButton v-if="!overlayOnly" variant="ghost" class="text-neutral whitespace-nowrap" @click="sideNavOpenPermanent = !sideNavOpenPermanent">
          <USwitch :model-value="!sideNavOpenPermanent" unchecked-icon="heroicons:arrow-left" checked-icon="heroicons:arrow-right" />
          {{ sideNavOpen ? $t('nav.main.util.minimizeSideBar') : '' }}
        </UButton>
        <UButton
          icon="heroicons:power"
          :variant="sideNavOpen ? 'solid' : 'ghost'"
          :label="sideNavOpen ? $t('button.logout') : ''"
          :class="{ 'ml-2': !sideNavOpen, 'mx-2': sideNavOpen, 'mt-4': overlayOnly }"
          @click="handleLogout"
        />
        <div v-if="sideNavOpen" class="flex items-center justify-center gap-1.5 text-xs text-dimmed">
          <span>{{ appVersion }}</span>
          <UBadge v-if="session?.otaBetaTester" :label="$t('nav.main.util.betaTester')" color="warning" variant="subtle" size="sm" />
        </div>
      </div>
    </div>
  </Transition>
</template>
